import type { TranslationMap } from "../types";
import en from "./locales/en.json";
import es from "./locales/es.json";

/** Built-in translations loaded from locale JSON files in `./locales/`. */
export const DEFAULT_TRANSLATIONS: TranslationMap = { en, es };

/** Human-readable language names keyed by BCP-47 base code. */
const LANGUAGE_NAMES: Record<string, string> = {
  en: "English",
  es: "Spanish",
  fr: "French",
  de: "German",
  pt: "Portuguese",
  it: "Italian",
  zh: "Chinese",
  ja: "Japanese",
  ko: "Korean",
  ar: "Arabic",
  hi: "Hindi",
  ru: "Russian",
};

/** Returns the human-readable English name for a language code. */
export function getLanguageName(code: string): string {
  return LANGUAGE_NAMES[code] ?? code;
}

/** Keys used (in order) by `resolveMessages` to build the initial-messages array. */
export const INITIAL_MESSAGE_KEYS = [
  "chat.greeting",
  "chat.lastConversation",
  "chat.helpPrompt",
] as const;

/**
 * Detect the user's preferred language from the browser.
 * Returns the base language code (e.g. "en" from "en-US").
 */
export function detectBrowserLanguage(): string {
  if (typeof navigator === "undefined") return "en";
  const lang = navigator.language ?? (navigator as { userLanguage?: string }).userLanguage ?? "en";
  return lang.split("-")[0].toLowerCase();
}

/**
 * Look up a single translation key.
 *
 * Resolution order:
 *  1. Key in the requested locale
 *  2. Key in the "en" locale
 *  3. The key itself (pass-through)
 */
export function t(key: string, language: string, translations: TranslationMap): string {
  return translations[language]?.[key] ?? translations["en"]?.[key] ?? key;
}

/**
 * Resolve initial messages for a given language code.
 *
 * Collects values for each key in `INITIAL_MESSAGE_KEYS`, falling back
 * through the locale → "en" → omit chain.
 */
export function resolveMessages(language: string, translations: TranslationMap): string[] {
  const locale = translations[language] ?? translations["en"] ?? {};
  const fallback = translations["en"] ?? {};
  return INITIAL_MESSAGE_KEYS.map((key) => locale[key] ?? fallback[key]).filter(
    (v): v is string => v != null,
  );
}
